<template>
    <div>
        <input
            ref="logo_upload"
            @change="uploadLogo"
            type="file"
            accept="image/*"
            style="display: none;"
        />
        <!-- Filters. Rebuilt on the shared KarroFin FilterBar: same endpoint,
             same params -- only the presentation moved. The Create action moves
             into the bar's own action slot, so the two no longer have to share
             a row and fight over its width. -->
        <FilterBar
          :show-search="false"
          :show-date="false"
          :advanced-count="activeChips.length"
          @change="reset_page_and_reload()"
          @reset="resetFilters"
        >
          <template #actions>
            <v-btn
              v-if="is_edit"
              class="elevation-0 white--text mr-2"
              color="success"
              small
              @click="openDialog()"
            >
              <v-icon left small>mdi-file-upload</v-icon>
              {{ isUploading ? 'Creating...' : 'Create New Product' }}
            </v-btn>
          </template>

          <div class="kf-field">
            <span class="kf-field__label">Loan type</span>
            <v-select
              v-model="loan_type"
              :items="loan_type_list"
              item-text="loan_type"
              item-value="loan_type"
              placeholder="Any loan type"
              dense
              outlined
              single-line
              hide-details
              clearable
              @change="reset_page_and_reload()"
            />
          </div>

          <div class="kf-field">
            <span class="kf-field__label">Status</span>
            <v-select
              v-model="status"
              :items="statusTypes"
              item-text="title"
              item-value="value"
              placeholder="Any status"
              dense
              outlined
              single-line
              hide-details
              clearable
              @change="reset_page_and_reload()"
            />
          </div>

          <template #chips>
            <v-chip
              v-for="c in activeChips"
              :key="c.key"
              small
              close
              class="kf-fchip"
              @click:close="clearFilter(c.key)"
            >
              <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
            </v-chip>
          </template>
        </FilterBar>
        <div v-if="can_reorder" class="text-caption grey--text mx-2 mb-1">
            <v-icon x-small>mdi-drag</v-icon>
            Drop a row onto another to place it immediately above that product &mdash; only
            those two and the products between them move. Use
            <v-icon x-small>mdi-dots-vertical</v-icon> to move a product to another page.
        </div>
        <div class="text-caption grey--text mx-2 mb-1">
            <v-icon x-small>mdi-eye-outline</v-icon>
            Rank counts every product, hidden ones too. "Customers see" is the place in
            the app and website: PL 2 = 2nd on the Personal Loan list.
        </div>
        <v-data-table
            :disable-sort="true"
            :headers="viewHeader(products_headers)"
            :items="products_list"
            :loading="table_loading"
            :items-per-page="page_length"
            class="border-a border-0 custome-header-bg"
            hide-default-footer
            loading-text="Loading...."
        >
            <template v-slot:item="{ item, index }">
                <tr
                    :key="item.id"
                    :draggable="can_reorder"
                    :class="{
                        'reorder-row': can_reorder,
                        'drag-over-row': drag_over_index === index && drag_index !== null,
                        'dragging-row': drag_index === index,
                    }"
                    @dragstart="onDragStart(index, $event)"
                    @dragover.prevent="onDragOver(index)"
                    @dragleave="onDragLeave(index)"
                    @drop="onDrop(index)"
                    @dragend="onDragEnd"
                    @click="viewProductDatails(item)"
                >
                    <td>
                        <div class="d-flex align-center">
                            <v-icon
                                v-if="can_reorder"
                                small
                                class="drag-handle mr-2"
                                @click.stop
                            >mdi-drag</v-icon>
                            <!-- Position in the global list, not the row index -
                                 it is the number the move actions speak. Hidden
                                 under a filter, where the visible rows are not
                                 contiguous and a rank would be a lie. -->
                            <span v-if="!is_filtered">{{ globalRank(index) }}</span>
                            <span v-else class="grey--text">&mdash;</span>
                        </div>
                    </td>
                    <!-- Server-computed over the whole catalogue, so it stays
                         true under any filter (see customerPosition). -->
                    <td>
                        <span
                            v-if="customerCell(item).hidden"
                            class="grey--text text-caption text-no-wrap"
                        >Hidden · {{ customerCell(item).hidden }}</span>
                        <div
                            v-else-if="customerCell(item).place"
                            :title="customerCell(item).title"
                        >
                            <div>#{{ customerCell(item).place }}</div>
                            <div class="grey--text text-caption text-no-wrap">
                                {{ customerCell(item).types }}
                            </div>
                        </div>
                        <span v-else class="grey--text">&mdash;</span>
                    </td>
                    <td>{{ item.id }}</td>
                    <td>
                        <img
                            v-if="item.logo"
                            class="mt-2"
                            height="50"
                            width="100"
                            :src="item.logo"
                            alt="Product logo"
                        >
                        <span v-else class="grey--text text-caption">No logo</span>
                    </td>
                    <td>{{ item.title }}</td>
                    <td>{{ item.product_type && item.product_type.loan_type }}</td>
                    <template v-if="is_edit">
                        <td>
                            <v-switch
                                inset
                                @click.stop
                                :input-value="item.is_active"
                                :disabled="Boolean(updatingProductIds[item.id])"
                                @change="(value) => updateProductData(item, 'is_active', value)"
                            ></v-switch>
                        </td>
                        <td>
                            <v-switch
                                inset
                                @click.stop
                                :input-value="item.flag_recomended"
                                :disabled="Boolean(updatingProductIds[item.id])"
                                @change="(value) => updateProductData(item, 'flag_recomended', value)"
                            ></v-switch>
                        </td>
                        <td class="text-center">
                            <!-- Uniform icon buttons in a wrapping flex row. The
                                 previous `text` buttons each reserved a 64px
                                 min-width and sized themselves to their content,
                                 so the five never lined up column to column. -->
                            <div class="action-buttons" @click.stop>
                                <!-- Outside the tooltip activator: a second root
                                     node in that slot is what pushed the sheet
                                     button out of line with the rest. -->
                                <input
                                    :id="'report_upload_' + item.id"
                                    @change="uploadFile"
                                    accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    type="file"
                                    style="display: none;"
                                />
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            class="elevation-0"
                                            icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                            @click="copyOfferLink(item.utm_link)"
                                         >
                                            <v-icon color="primary">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Copy UTM Link</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            class="elevation-0"
                                            icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                            @click="triggerLogoUpload(item)"
                                         >
                                            <v-icon color="primary">mdi-upload</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Update Product Logo</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            class="elevation-0"
                                            icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                            @click="editLoanStatus(item)"
                                        >
                                            <v-icon color="primary">mdi-pencil</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit Product Details</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            class="elevation-0"
                                            icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                            @click="triggerSheetUpload(item.id)"
                                            :loading="isUploading"
                                            :disabled="isUploading"
                                        >
                                            <v-icon color="primary">mdi-file-upload</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Upload Sheet</span>
                                </v-tooltip>
                                <!-- Moves a drag cannot reach: dragging only
                                     covers the ten rows on screen, so crossing a
                                     page boundary needs an explicit target. -->
                                <v-menu offset-y left>
                                    <template v-slot:activator="{ on: menu, attrs }">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on: tooltip }">
                                                <v-btn
                                                    class="elevation-0"
                                                    icon
                                                    small
                                                    :disabled="!can_reorder || is_moving"
                                                    v-bind="attrs"
                                                    v-on="{ ...menu, ...tooltip }"
                                                >
                                                    <v-icon color="primary">mdi-swap-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Move Product</span>
                                        </v-tooltip>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="moveToTop(item)">
                                            <v-icon small class="mr-2">mdi-chevron-double-up</v-icon>
                                            <v-list-item-title>Move to top</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item
                                            v-if="!is_filtered"
                                            :disabled="page_number <= 1"
                                            @click="movePageUp(item, index)"
                                        >
                                            <v-icon small class="mr-2">mdi-arrow-up</v-icon>
                                            <v-list-item-title>Move up a page</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item
                                            v-if="!is_filtered"
                                            :disabled="page_number >= total_page_count"
                                            @click="movePageDown(item, index)"
                                        >
                                            <v-icon small class="mr-2">mdi-arrow-down</v-icon>
                                            <v-list-item-title>Move down a page</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="moveToBottom(item)">
                                            <v-icon small class="mr-2">mdi-chevron-double-down</v-icon>
                                            <v-list-item-title>Move to bottom</v-list-item-title>
                                        </v-list-item>
                                        <v-divider v-if="!is_filtered"></v-divider>
                                        <v-list-item
                                            v-if="!is_filtered"
                                            @click="openMoveToPositionDialog(item, index)"
                                        >
                                            <v-icon small class="mr-2">mdi-format-list-numbered</v-icon>
                                            <v-list-item-title>Move to position&hellip;</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </td>
                    </template>
                </tr>
            </template>
        </v-data-table>
        <v-pagination
            v-model="page_number"
            :length="total_page_count"
            :total-visible="7"
            @input="get_products_list()"
        ></v-pagination>
        <v-dialog v-model="move_dialog" max-width="360" persistent>
            <v-card>
                <v-card-title class="text-subtitle-1">
                    Move {{ move_target && move_target.title }}
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="move_to_rank"
                        label="Position"
                        type="number"
                        min="1"
                        :max="total_count"
                        :hint="'1 is shown first, ' + total_count + ' is last'"
                        persistent-hint
                        outlined
                        dense
                        autofocus
                        @keyup.enter="confirmMoveToPosition()"
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="move_dialog = false">Cancel</v-btn>
                    <v-btn color="primary" text @click="confirmMoveToPosition()">Move</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <createNewProductDialog ref="createNewProductDialog" @created="onProductCreated" />
        <editProductDetails ref="editProductDetails" @updated="get_products_list" />
    </div>
</template>
<script>
import { mapGetters } from "vuex";
import createNewProductDialog from "../../components/products/createNewProductDialog.vue";
import editProductDetails from "../../components/products/editProductDetails.vue";
import keys from "../../app-js/keys";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import { customerPosition } from "@/app-js/karrofin/api/normalise";
export default {
    name: "Products",
    components: {
        FilterBar,
        createNewProductDialog,
        editProductDetails,
    },
    data() {
        return {
           product_id: null,
           products_list: [],
           drag_index: null,
           drag_over_index: null,
           table_loading: false,
           page_number: 1,
           loan_type: '',
           total_page_count: 0,
           total_count: 0,
           // Seeded from the shared key so the table, the pager and the
           // page_length we send the server can never disagree - the previous
           // copy was a hardcoded 10 that was never sent at all.
           page_length: keys.PAGE_LENGTH,
           is_moving: false,
           move_dialog: false,
           move_target: null,
           move_to_rank: null,
           isUploading: false,
           status: null,
           is_edit: false,
           updatingProductIds: {},
           loan_type_list: [],
           products_headers: [
                { text: "Rank", value: "rank", align: "start", sortable: false, class: "primary_2 white--text", },
                { text: "Customers See", value: "customer_rank", align: "start", sortable: false, class: "primary_2 white--text", },
                { text: "Product ID", value: "id", align: "start", sortable: false, class: "primary_2 white--text", },
                { text: "Logo", value: "logo", align: "start", sortable: false , class: "primary_2 white--text",},
                { text: "Name", value: "title", align: "start", sortable: false , class: "primary_2 white--text",},
                { text: "Loan Type", value: "product_type.loan_type", align: "start", sortable: false, class: "primary_2 white--text", }, 
                { text: "Is Active", value: "is_active", align: "start", sortable: false, class: "primary_2 white--text", }, 
                { text: "Recomended", value: "flag_recomended", align: "start", sortable: false, class: "primary_2 white--text", },
                { text: "Actions", value: "actions", align: "center", sortable: false, class: "primary_2 white--text", },
            ],
            statusTypes: [
                {
                    title: 'Active',
                    value: true
                },
                {
                    title: 'Inactive',
                    value: false
                }
            ],
        };
    },
    mounted() {
        this.get_products_list();
        this.getLoanType();
    },
    created() {
      let permission_list = JSON.parse(sessionStorage.getItem("permission_list")) || [];
        if (permission_list.includes("Products Edit")) {
            this.is_edit = true;
        }  
    },
    watch: {
        search_query() {
            this.reset_page_and_reload();
        },
    },
    computed: {
        ...mapGetters({
            search_query: "getSearchText",
            filters: "product/getFilters",
        }),
        /** One entry per applied filter, for the chip row. */
        activeChips() {
            const chips = [];
            if (this.loan_type) {
                chips.push({
                    key: "loan_type",
                    label: "Loan type",
                    value: this.loan_type,
                });
            }
            // Inactive is `false`, a real selection -- a truthiness test would
            // drop its chip and leave the filter applied with nothing saying so.
            if (this.status !== null && this.status !== undefined && this.status !== "") {
                const match = this.statusTypes.find((s) => s.value === this.status);
                chips.push({
                    key: "status",
                    label: "Status",
                    value: match ? match.title : String(this.status),
                });
            }
            return chips;
        },
        // A move is anchored on the product it is dropped onto, and that anchor
        // is resolved against the global ranking server-side - so it stays
        // correct under a search or a filter, where the visible rows are not
        // contiguous. Edit permission is the only gate.
        can_reorder() {
            return this.is_edit;
        },
        // to_rank is a position in the global list, so the page-jump actions
        // only mean anything when the list is not being filtered down.
        is_filtered() {
            return Boolean(this.search_query || this.loan_type || this.status !== null);
        },
    },
    methods: {
        /** One chip's x. */
        clearFilter(key) {
            this[key] = null;
            this.reset_page_and_reload();
        },
        /** Clears both filters. */
        resetFilters() {
            this.loan_type = null;
            this.status = null;
            this.reset_page_and_reload();
        },
        triggerSheetUpload(id) {
            this.product_id = id;
            document.getElementById('report_upload_' + id).click();
        },
        triggerLogoUpload(item) {
            this.product_id = item.id;
            const fileInput = this.$refs.logo_upload;
            if (fileInput) {
                fileInput.value = "";
                fileInput.click();
            }
        },
        /** The "Customers see" cell: the loan-type filter picks the headline. */
        customerCell(item) {
            return customerPosition(item, this.loan_type || null);
        },
        viewHeader(headers) {
            return this.is_edit
                ? headers
                : headers.filter(h => h.value !== "actions" && h.value !== "is_active" && h.value !== "flag_recomended");
        },
        openDialog() {
            this.$refs.createNewProductDialog.openDialog();  
        },
        onProductCreated() {
            this.page_number = 1;
            this.status = null;
            this.loan_type = "";
            this.get_products_list();
        },
        getLoanType() {
            const self = this;

            const params = { flag_active: false };

            const successHandler = (response) => {
                self.loan_type_list = response.data.result;
            };

            self.request_GET(
                self,
                self.$urls.GET_LOAN_TYPE,
                params,
                successHandler,
                null,
                null
            );
        },
        uploadLogo() {
            const fileInput = this.$refs.logo_upload;
            const lookup_key = this.product_id;
            const file = fileInput && fileInput.files ? fileInput.files[0] : null;

            if (!file || !lookup_key) {
                return;
            }

            const formData = new FormData();
            formData.append("logo", file);

            const self = this;

            const successHandler = () => {
                self.showSnakeBar("success", "Product logo updated successfully");
                self.get_products_list();
                fileInput.value = "";
            };

            const errorHandler = () => {
                self.showSnakeBar("error", "Failed to upload product logo");
                fileInput.value = "";
            };

            self.request_PATCH(
                self,
                self.$urls.UPDATE_ADMIN_PRODUCT_DATA,
                `${lookup_key}/`,
                formData,
                successHandler,
                null,
                errorHandler,
                null
            );
        },
        uploadFile(event) {
            const fileInput = event.target;
            const file = fileInput.files[0];

            if (!file) {
                return;
            }

            this.isUploading = true;
            const formData = new FormData();
            
            formData.append('product', this.product_id);
            formData.append('file', file);
            

            const self = this;

            const successHandler = (response) => {
                console.log('File uploaded successfully:', response);
                self.showSnakeBar("success", "Sheet Successfully uploaded");
                self.get_products_list();
                fileInput.value = '';
            };

            const errorHandler = (error) => {
                console.log("failure", error);
                self.showSnakeBar("error", "Could not upload sheet");
            };

            const finallyHandler = () => {
                self.isUploading = false;
            };
            self.request_POST(
                self,
                self.$urls.UPLOAD_PRODUCT_SHEET,
                formData,
                successHandler,
                null,
                errorHandler,
                finallyHandler
            );
        },
        copyOfferLink(url) {
            if(url){
                navigator.clipboard.writeText(url);
                this.$store.dispatch("snackbar/setSnackbar", {
                flag: true,
                text:"UTM Link Copied Successfully",
                color: "success",
                });
            } else {
                this.$store.dispatch("snackbar/setSnackbar", {
                flag: true,
                text:"UTM Link Not Found",
                color: "error",
                });
            }
        },
        updateProductData(item, field, value) {
            if (this.updatingProductIds[item.id]) {
                return;
            }

            const self = this;
            const lookup_key = item.id;
            const previousValue = item[field];
            const formData = { [field]: value };

            this.$set(item, field, value);
            this.$set(this.updatingProductIds, item.id, true);

            const successHandler = () => {
                self.showSnakeBar("success", "Product updated successfully");
                self.get_products_list();
            };

            const errorHandler = () => {
                self.$set(item, field, previousValue);
                self.showSnakeBar("error", "Failed to update product");
            };

            const finallyHandler = () => {
                self.$set(self.updatingProductIds, item.id, false);
            };

            self.request_PATCH(
                self,
                self.$urls.UPDATE_ADMIN_PRODUCT_DATA,
                `${lookup_key}/`,
                formData,
                successHandler,
                null,
                errorHandler,
                finallyHandler
            );
        },
        // Filters and search re-slice the whole result set, so staying on the
        // page the admin happened to be on lands them on a clamped or empty
        // page while the pager still reads 5.
        reset_page_and_reload() {
            this.page_number = 1;
            this.get_products_list();
        },
        get_products_list() {
            const self = this;
            self.table_loading = true;
            const param = {
                search: self.search_query,
                page_number: self.page_number,
                page_length: self.page_length,
                loan_type: self.loan_type,
                is_active: self.status
            }
            const successHandler = (response) => {
                const result = response.data.result;
                self.products_list = result.data;
                self.total_page_count = result.total_page_count;
                self.total_count = result.total_count;
                // Trust the server's page size over ours - globalRank() is only
                // right if the two agree.
                if (result.page_length) {
                    self.page_length = result.page_length;
                }
            };
            const finallyHandler = () => {
                self.table_loading = false;
            }
            self.request_GET(
                self,
                self.$urls.GET_ADMIN_PRODUCT_LIST,
                param,
                successHandler,
                null,
                null,
                finallyHandler
            );
        },
        editLoanStatus(item) {
            this.$refs.editProductDetails.openDialog(item);
        },
        viewProductDatails(item) {
            // ignore the click that ends a drag gesture
            if (this.drag_index !== null) {
                return;
            }
            this.filters.productDetail = item;
            this.filters.allow_edit = false;
            this.$router.push({
                name: "product",
                params: { id: item.id },
            });
        },
        onDragStart(index, event) {
            if (!this.can_reorder) {
                return;
            }
            this.drag_index = index;
            if (event && event.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                // Firefox needs data set for a drag to actually start
                event.dataTransfer.setData("text/plain", String(index));
            }
        },
        onDragOver(index) {
            if (!this.can_reorder || this.drag_index === null) {
                return;
            }
            this.drag_over_index = index;
        },
        onDragLeave(index) {
            if (this.drag_over_index === index) {
                this.drag_over_index = null;
            }
        },
        onDrop(index) {
            if (!this.can_reorder || this.drag_index === null) {
                return;
            }
            const from = this.drag_index;
            const to = index;
            this.drag_over_index = null;
            if (from === to) {
                this.drag_index = null;
                return;
            }

            const moved = this.products_list[from];
            const anchor = this.products_list[to];
            if (!moved || !anchor) {
                this.drag_index = null;
                return;
            }

            // Send an anchor, not a new page order. "Put this product
            // immediately above/below that one" is well defined whatever page
            // we are on and whatever filter is applied, so the server can move
            // exactly one product through the global ranking. Posting the
            // visible page instead only ever let the server shuffle the ranks
            // that page already held, which could not cross a page boundary and
            // dragged every filtered-out product past the one being moved.
            const payload = { product_id: moved.id };
            if (from > to) {
                payload.before_id = anchor.id;
            } else {
                payload.after_id = anchor.id;
            }

            // Optimistic reorder of the visible rows; get_products_list() below
            // replaces them with the server's answer either way.
            const list = this.products_list.slice();
            list.splice(from, 1);
            list.splice(to, 0, moved);
            this.products_list = list;

            // keep drag_index set until after the router-guard click check clears in onDragEnd
            this.moveProduct(payload);
        },
        onDragEnd() {
            this.drag_index = null;
            this.drag_over_index = null;
        },
        /**
         * Move one product to a position defined relative to the others.
         *
         * `payload` carries the product plus exactly one anchor: before_id,
         * after_id, to_rank (1-based global position) or to: 'top' | 'bottom'.
         */
        moveProduct(payload) {
            const self = this;
            if (self.is_moving) {
                return;
            }
            self.is_moving = true;

            const successHandler = (response) => {
                if (response.data && response.data.success === false) {
                    return;
                }
                self.showSnakeBar("success", "Product order updated successfully");
            };
            const errorHandler = (error) => {
                const message =
                    self.getApiErrorMessage(error) || "Failed to update product order";
                self.showSnakeBar("error", message);
            };
            const finallyHandler = () => {
                self.is_moving = false;
                // Always re-read: on success to pick up the shifted neighbours,
                // on failure to undo the optimistic splice above.
                self.get_products_list();
            };

            self.request_POST(
                self,
                self.$urls.MOVE_ADMIN_PRODUCT,
                payload,
                successHandler,
                null,
                errorHandler,
                finallyHandler
            );
        },
        /**
         * A row's 1-based position in the GLOBAL ranking, which is the unit the
         * move API's to_rank speaks. Only meaningful without a search/filter,
         * which is why the page-jump actions are hidden then.
         */
        globalRank(index) {
            return (this.page_number - 1) * this.page_length + index + 1;
        },
        moveToTop(item) {
            this.moveProduct({ product_id: item.id, to: "top" });
        },
        moveToBottom(item) {
            this.moveProduct({ product_id: item.id, to: "bottom" });
        },
        movePageUp(item, index) {
            // Ranks past either end are clamped server-side, so this is safe on
            // the first and last page.
            this.moveProduct({
                product_id: item.id,
                to_rank: this.globalRank(index) - this.page_length,
            });
        },
        movePageDown(item, index) {
            this.moveProduct({
                product_id: item.id,
                to_rank: this.globalRank(index) + this.page_length,
            });
        },
        openMoveToPositionDialog(item, index) {
            this.move_target = item;
            this.move_to_rank = this.globalRank(index);
            this.move_dialog = true;
        },
        confirmMoveToPosition() {
            const rank = Number(this.move_to_rank);
            this.move_dialog = false;
            if (!this.move_target || !rank || rank < 1) {
                return;
            }
            this.moveProduct({ product_id: this.move_target.id, to_rank: rank });
        },
    },
};
</script>
<style scoped>
/* Five equal-sized icon buttons on one line, wrapping to two only when the
   column is squeezed. inline-flex keeps the cell from stretching the row. */
.action-buttons {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    /* 5 * 28px button + 4 * 4px gap; below this the row splits cleanly in two
       rather than leaving one button orphaned. */
    max-width: 156px;
}
.reorder-row {
    cursor: pointer;
}
.reorder-row .drag-handle {
    cursor: grab;
}
.reorder-row .drag-handle:active {
    cursor: grabbing;
}
.dragging-row {
    opacity: 0.5;
}
.drag-over-row {
    border-top: 2px solid var(--v-primary-base, #1976d2);
    background-color: rgba(25, 118, 210, 0.06);
}
</style>